import { Injectable } from "@angular/core";
import { HttpClient,HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
// Env
import { environment } from "src/environments/environment";
// Utils
import { constants } from "../utils/constants";
import { api } from "../utils/api";
import helpers from "../utils/helpers";
// Model
import { Account, ICompany, IAdmin } from "../models/account.model";

@Injectable({
  providedIn: "root",
})
export class AccountService {
  private GENERATE_TOKEN = environment.API_URL + api.account.create.generateToken;
  private GET_COMPANY = environment.API_URL + api.account.create.getCompanyData;
  private VERIFY_TOKEN = environment.API_URL + api.account.create.verifyToken;
  private VERIFY_COMPANY = environment.API_URL + api.account.create.verifyCompany;
  private CREATE_ACCOUNT = environment.API_URL + api.account.create.account;
  private CREATE_PASSWORD = environment.API_URL + api.account.create.password;
  private EMAIL_URL = environment.API_URL + api.users.email;

  private emailTemplates = {
    admin: "BFPE_EXTRANET_ALTA_USUARIO",
    company: "BFPE_EXTRANET_ALTA_EMPRESA",
  };

  validCompany = false;
  account: Account = new Account();

  constructor(private _httpClient: HttpClient) {}

  // =================================================================
  //                      GETTERS & SETTERS
  // =================================================================
  setCompanyData(company: ICompany) {
    this.account.company = company;
  }

  getCompanyData(): ICompany {
    return this.account.company;
  }

  setAdminData(admin: IAdmin) {
    this.account.admin = admin;
  }

  getAdminData(): IAdmin {
    return this.account.admin;
  }

  reset() {
    this.account = new Account();
  }

  /**
   * Obtiene los datos de una empresa para autocompletar el formulario, a partir del RUC.
   */
  getCompanyByRuc(ruc: number): Observable<ICompany> {
    const url = this.GET_COMPANY + ruc;
    // return of(institution).pipe(
    return this._httpClient.get(url).pipe(
      map((resp: any) => {
        if (!resp?.enterpriseCustomer) {
          throw constants.alerts.verifyCompany.nonExistentCompany;
        }
        const company: ICompany = {
          ruc,
          name: resp.enterpriseCustomer.businessName,
          area: resp.enterpriseCustomer.sector.name,
          phone: resp.enterpriseCustomer.telephone[0].number,
          segment: resp.segment.name,
          address: resp.enterpriseCustomer.address[0].fullAddress,
          email: resp.enterpriseCustomer.email[0].emailAddress,
        };
        this.setCompanyData(company);
        return company;
      }),
      catchError((err) => {
       
        if (err.type) {
          throw err;
        }
        throw constants.alerts.default;
      })
    );
  }

  // =================================================================
  //                          VERIFY
  // =================================================================
  /**
   * Invoca al servicio para validar el token previamente generado.
   */
  verifyToken(code: number): Observable<any> {
    
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "userToken": "",
    });
    const body = {
      valueToken: String(code),
      identificationType: this.getAdminData().docType,
      userId: this.getAdminData().docNumber, // userId = docNumber
    };

    return this._httpClient.post(this.VERIFY_TOKEN, body, { headers }).pipe(
      map((resp: any) => {
        if (resp.Detail || resp.generic?.processResultCode !== 201) {
          throw new Error(resp.Detail?.errors?.error?.cmmCode);
        }
      }),
      catchError((err: any) => {
        throw this.getError(err.message);
      })
    );
  }

  verifyCompany(ruc: number): Observable<any> {

    const headers = new HttpHeaders({
        "Content-Type": "application/json",
        "userToken": "",
    });

    const body = {
      identificationNumber: ruc,
      identificationType: "RUC",
    };
    

    return this._httpClient.post(this.VERIFY_COMPANY, body, { headers }).pipe(
      tap((resp: any) => {
        //Error de servicio
        if (resp.Detail) throw constants.alerts.default;
        // Si existe la empresa, quedarse con el id
        if (resp?.generic?.hasNotExecutives)
          throw constants.alerts.verifyCompany.existingCompany.withAdmin;

        this.account.company.id = resp?.institution?.sIntitutionId;
      }),
      catchError((err) => {
        if (err.type) {
          throw err;
        }
        throw constants.alerts.default;
      })
    );
  }

  // =================================================================
  //                          CREATE
  // =================================================================

  createAccount(): Observable<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "userToken": "",
    });
    return this._httpClient
      .post(this.CREATE_ACCOUNT, this.getCreateAccountBody(),{ headers })
      .pipe(
        tap((resp) => {
          // Si no es {} hubo un error
          if (!helpers.emptyResponse(resp)) throw constants.alerts.default;
          // Notificar por mail a la empresa
          this.sendEmail({
            firstName: this.account.admin.firstName,
            lastName: this.account.admin.lastName,
            docType: this.account.admin.docType,
            docNumber: this.account.admin.docNumber,
            email: this.account.company.email, //empresa
            ruc: this.account.company.ruc,
            companyName: this.account.company.name,
            templateId: this.emailTemplates.company,
          });
          // Notificar por mail al operador
          this.sendEmail({
            firstName: this.account.admin.firstName,
            lastName: this.account.admin.lastName,
            docType: this.account.admin.docType,
            docNumber: this.account.admin.docNumber,
            email: this.account.admin.email, //admin
            ruc: this.account.company.ruc,
            companyName: this.account.company.name,
            templateId: this.emailTemplates.admin,
          });
        }),
        catchError((err: any) => {
          throw constants.alerts.default;
        })
      );
  }

  /**
   * Invoca al servicio para crear un token y enviarlo por teléfono
   */
  generateToken(data: {
    ruc: number;
    docType: string;
    docNumber: string;
  }): Observable<any> {
    // Setea data en memoria para el verifyToken
    this.setCompanyData({ ...this.getCompanyData(), ruc: data.ruc });
    this.setAdminData({
      ...this.getAdminData(),
      docType: data.docType,
      docNumber: data.docNumber,
    });

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "userToken": "",
    });

    const body = {
      identificationNumberCustomer: data.docNumber,
      identificationTypeCustomer: data.docType,
      identificationNumberInstitution: data.ruc,
      identificationTypeInstitution: "RUC",
    };

    return this._httpClient.post(this.GENERATE_TOKEN, body,{ headers }).pipe(
      tap((resp: any) => {
        if (resp.Detail) {
          throw new Error(resp.Detail?.errors?.error?.cmmCode);
        }
        this.setAdminData({
          ...this.getAdminData(),
          phone: resp.systemUser?.contactCelPhone?.phoneNumber?.toString(),
        });
      }),
      catchError((err: any) => {
        
        throw this.getError(err.message);
      })
    );
  }


  

  /**
   * Invoca al servicio para crear/recuperar/desbloquear una password
   */
  createPassword(password: number): Observable<any> {

    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "userToken": "",
    });

    const body = {
      customerIdentificationNumber: this.account.admin.docNumber,
      customerIdentificationType: this.account.admin.docType,
      customerPhoneNumber: this.account.admin.phone,
      institutionIdentificationNumber: this.account.company.ruc,
      institutionIdentificationType: "RUC",
      authenticationAnswer: password,

    };
    return this._httpClient.post(this.CREATE_PASSWORD, body, { headers }).pipe(
      map((resp: any) => {
              
        if (resp?.generic?.processResultCode !== 201) {

          if(resp?.Detail?.errors?.error.sourceCode === 465){
             throw constants.alerts.passParteTel;
          }else if(resp?.Detail?.errors?.error.sourceCode === 466){
              throw constants.alerts.passParteDoc;
          }else{
            throw constants.alerts.default;
          }
          
        }
      }),
      catchError((err: any) => {
        
         if(err?.code === 465){
            throw constants.alerts.passParteTel;
         }else if(err?.code === 466){
             throw constants.alerts.passParteDoc;
         }else{
           throw constants.alerts.default;
         }
      })
    );
  }

  // =================================================================
  //                          Errors
  // =================================================================
  /**
   * Obtiene el error correspondiente desde response, a partir de un cmmCODE
   */
  private getError(cmmCode: string) {
    switch (cmmCode) {
      case "OTP_NOT_VALID":
        return constants.alerts.invalidToken;

      case "SMS_NOT_VALID":
        return constants.alerts.expiredToken;

      case "SMS_BLOCKED":
        return constants.alerts.blockedSms;

      case "UNKNOWN_ERROR":
        return constants.alerts.credentials;

      default:
        return constants.alerts.default;
    }
  }

  // =================================================================
  //                          HELPERS
  // =================================================================
  
  private sendEmail(data: {
    firstName: string;
    lastName: string;
    docType: string;
    docNumber: any;
    email: string;
    ruc: any;
    companyName: string;
    templateId: string;
  }) {
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "userToken": "",
    });

    
    const body = {
      firstNameSource: data.firstName+" "+data.lastName,
      lastNameSource: data.lastName,
      identificationNumberSource: data.docNumber,
      identificationTypeSource: "DNI",
      emailAddressComplete: data.email,
      firstNameTarget: data.companyName,
      identificationNumberTarget: data.ruc,
      identificationTypeTarget: "RUC",
      executionDate: helpers.internalFormatFromDate(new Date()),
      templateId: data.templateId,
    };
    this._httpClient.post(this.EMAIL_URL, body, { headers }).subscribe();
  }

  private getCreateAccountBody() {
    const adminBody = this.getCreateAdminBody();
    if (this.account.company.id) {
      // Solo se envia Administrador
      return { ...adminBody, institutionId: this.account.company.id };
    }
    // Se envia todo
    return {
      ...adminBody,
      institutionCountry: "PER",
      institutionIdentificationType: "RUC",
      institutionBusinessSector: this.account.company.area,
      institutionIdentificationNumber: this.account.company.ruc,
      institutionAddress: this.account.company.address,
      institutionEmailAddress: this.account.company.email,
      institutionContactPhone: this.account.company.phone,
      institutionName: this.account.company.name,
      institutionSegment: this.account.company.segment,
    };
  }

  private getCreateAdminBody() {
    return {
      customerCountry: "PER",
      customerContactPhone: this.account.admin.phone,
      customerFirstName: this.account.admin.firstName,
      customerLastName: this.account.admin.lastName,
      customerIdentificationType: this.account.admin.docType,
      customerIdentificationNumber: this.account.admin.docNumber,
      customerEmailAddress: this.account.admin.email,
      customerUserPosition: this.account.admin.charge,
    };
  }
}
